import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useWebSocket } from '../../contexts/WebSocketContext';
import api, { notificationsApi } from '../../lib/api';
import { Bell, LogOut, ChevronDown, Wifi, WifiOff, UserCog, MessageSquareWarning } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { getRoleLabel } from '../../lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

export const Header = ({ title }) => {
  const { user, logout } = useAuth();
  const { isConnected, notifications: wsNotifications, unreadCount: wsUnreadCount, setUnreadCount } = useWebSocket();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setLocalUnreadCount] = useState(0);
  // iter132 — "My pending threads" badge: count of doc threads where this user is
  // a status_assignee AND has NOT personally approved yet (status_approved_by ∌ user).
  const [pendingThreads, setPendingThreads] = useState({ count: 0, request_count: 0, requests: [] });
  const fetchPendingThreads = useCallback(async () => {
    try {
      const res = await api.get('/me/pending-doc-threads-count');
      setPendingThreads(res.data || { count: 0, request_count: 0, requests: [] });
    } catch (e) {
      setPendingThreads({ count: 0, request_count: 0, requests: [] });
    }
  }, []);
  useEffect(() => {
    fetchPendingThreads();
    // Refresh every 60s so the count stays approximately fresh without WebSocket plumbing.
    const interval = setInterval(fetchPendingThreads, 60_000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await notificationsApi.getAll();
      setNotifications(response.data.slice(0, 10));
      setLocalUnreadCount(response.data.filter(n => !n.is_read).length);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  // Merge WebSocket notifications with fetched notifications
  useEffect(() => {
    if (wsNotifications.length > 0) {
      setNotifications(prev => {
        const wsIds = new Set(wsNotifications.map(n => n.id));
        const filtered = prev.filter(n => !wsIds.has(n.id));
        return [...wsNotifications, ...filtered].slice(0, 10);
      });
    }
  }, [wsNotifications]);

  // Update unread count from WebSocket
  useEffect(() => {
    if (wsUnreadCount > 0) {
      setLocalUnreadCount(prev => prev + wsUnreadCount);
    }
  }, [wsUnreadCount]);

  const handleMarkAllRead = async () => {
    try {
      await notificationsApi.markAllRead();
      setLocalUnreadCount(0);
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (error) {
      console.error('Failed to mark notifications as read:', error);
    }
  };

  const handleNotificationClick = async (notif) => {
    if (!notif.is_read) {
      try {
        await notificationsApi.markRead(notif.id);
        setNotifications(prev => prev.map(n => 
          n.id === notif.id ? { ...n, is_read: true } : n
        ));
        setLocalUnreadCount(prev => Math.max(0, prev - 1));
      } catch (error) {
        console.error('Failed to mark notification as read:', error);
      }
    }
    if (notif.request_id) {
      // Build navigation URL with optional comment highlight
      let url = `/requests/${notif.request_id}`;
      if (notif.comment_id) {
        url += `?tab=discussion&comment=${notif.comment_id}`;
        if (notif.template_key === 'doc_review_nudge') url += '&highlight=nudge';
      }
      navigate(url);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const totalUnread = unreadCount || wsUnreadCount;

  return (
    <header className="header" data-testid="header">
      <div className="flex items-center gap-4">
        <h1 className="text-xl font-semibold text-slate-900 font-['Manrope']">{title}</h1>
      </div>

      <div className="flex items-center gap-3">
        {/* WebSocket Status Indicator */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center">
                {isConnected ? (
                  <Wifi size={16} className="text-emerald-500" />
                ) : (
                  <WifiOff size={16} className="text-slate-400" />
                )}
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>{isConnected ? 'Real-time notifications connected' : 'Connecting to notifications...'}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* iter132 — My pending threads badge: click to see request breakdown. */}
        <DropdownMenu onOpenChange={(open) => { if (open) fetchPendingThreads(); }}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="relative"
              data-testid="pending-threads-btn"
              title={pendingThreads.count > 0 ? `${pendingThreads.count} doc thread${pendingThreads.count === 1 ? '' : 's'} awaiting your review` : 'No threads pending your review'}
            >
              <MessageSquareWarning size={20} className={pendingThreads.count > 0 ? 'text-amber-600' : 'text-slate-400'} />
              {pendingThreads.count > 0 && (
                <span
                  className="absolute -top-0.5 -right-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-semibold text-white"
                  data-testid="pending-threads-badge"
                >
                  {pendingThreads.count > 9 ? '9+' : pendingThreads.count}
                </span>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-96">
            <DropdownMenuLabel className="flex items-center gap-2">
              <MessageSquareWarning className="w-4 h-4 text-amber-600" />
              <span>My Pending Doc Threads</span>
              {pendingThreads.count > 0 && (
                <span className="ml-auto text-xs font-normal text-slate-500">
                  {pendingThreads.count} across {pendingThreads.request_count} request{pendingThreads.request_count === 1 ? '' : 's'}
                </span>
              )}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {pendingThreads.count === 0 ? (
              <div className="p-6 text-center text-sm text-slate-500" data-testid="pending-threads-empty">
                <MessageSquareWarning className="w-7 h-7 mx-auto mb-2 text-slate-300" />
                You&rsquo;re all caught up — no thread reviews waiting on you.
              </div>
            ) : (
              <div className="max-h-80 overflow-y-auto">
                {pendingThreads.requests.map((r) => (
                  <DropdownMenuItem
                    key={r.request_id}
                    onClick={() => navigate(`/requests/${r.request_id}?tab=discussion`)}
                    className="flex items-start gap-2 cursor-pointer py-2"
                    data-testid={`pending-threads-item-${r.request_id}`}
                  >
                    <span className="mt-0.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-100 text-amber-800 text-[11px] font-semibold border border-amber-200 px-1">
                      {r.thread_count}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-slate-800 truncate">
                        {r.ref_no} <span className="text-slate-400 font-normal">• {r.type}</span>
                      </div>
                      <div className="text-xs text-slate-500 truncate">{r.title || 'Untitled'}</div>
                    </div>
                  </DropdownMenuItem>
                ))}
              </div>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Notifications */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
              <Bell size={20} className="text-slate-600" />
              {totalUnread > 0 && (
                <span className="notification-dot animate-pulse">{totalUnread > 9 ? '9+' : totalUnread}</span>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-96">
            <DropdownMenuLabel className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span>Notifications</span>
                {isConnected && (
                  <span className="flex items-center gap-1 text-xs text-emerald-600">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                    Live
                  </span>
                )}
              </div>
              {totalUnread > 0 && (
                <button 
                  onClick={handleMarkAllRead}
                  className="text-xs text-blue-600 hover:underline"
                >
                  Mark all read
                </button>
              )}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <div className="max-h-80 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="px-4 py-6 text-center text-sm text-slate-500">
                  <Bell className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                  No notifications yet
                </div>
              ) : (
                notifications.map((notif) => (
                  <DropdownMenuItem 
                    key={notif.id} 
                    className={`flex flex-col items-start gap-1 cursor-pointer p-3 ${!notif.is_read ? 'bg-blue-50 border-l-2 border-blue-500' : ''}`}
                    onClick={() => handleNotificationClick(notif)}
                  >
                    <span className={`text-sm ${!notif.is_read ? 'font-medium text-slate-900' : 'text-slate-700'}`}>
                      {notif.message}
                    </span>
                    {notif.preview && (
                      <span className="text-xs text-slate-500 line-clamp-2 italic bg-slate-50 px-2 py-1 rounded w-full">
                        &ldquo;{notif.preview}&rdquo;
                      </span>
                    )}
                    <span className="text-xs text-slate-400">
                      {new Date(notif.sent_at).toLocaleString()}
                    </span>
                  </DropdownMenuItem>
                ))
              )}
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              className="text-center text-blue-600 cursor-pointer justify-center"
              onClick={() => navigate('/notifications')}
            >
              View all notifications
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
              <div className="w-8 h-8 rounded-full bg-blue-900 flex items-center justify-center">
                <span className="text-sm font-medium text-white">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="hidden md:block text-left">
                <p className="text-sm font-medium text-slate-700">{user?.name}</p>
                <p className="text-xs text-slate-500">{getRoleLabel(user?.role)}</p>
              </div>
              <ChevronDown size={16} className="text-slate-400" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>My Account</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/profile')} className="cursor-pointer" data-testid="user-menu-profile">
              <UserCog size={16} className="mr-2" />
              My Profile &amp; Documents
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer">
              <LogOut size={16} className="mr-2" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};
